import * as React from "react";
import * as S from "@styled";
import { Diff, DiffArray, DiffEdit } from "deep-diff";

import { IProcessData } from "../processEditor/model/IProcessData";
import DiffMonacoEditor from "../shared/monaco/components/DiffMonacoEditor";
import { ParameterEditorMode } from "../processEditor/components/ParameterInput";
import CustomModal from "shared/components/CustomModal";

export class DiffElementModal extends React.Component<DiffModalProps, DiffModalState> {
    constructor(props: DiffModalProps) {
        super(props);
        this.state = {
            selectedElementId: null
        };
    }

    private isDiffArray = (kind: DiffArray<DiffArrayNameValue, DiffArrayNameValue> | DiffEditObject): kind is DiffArray<DiffArrayNameValue, DiffArrayNameValue> => (kind as DiffArray<DiffArrayNameValue, DiffArrayNameValue>).item !== undefined;

    private getDiffArrayValue = (action: Diff<DiffArrayNameValue, DiffArrayNameValue>): { lhs?: string, rhs?: string } | null => {
        switch (action.kind) {
            // Deleted entry
            case "D": {
                const lhs = {
                    name: action.lhs.Name.value.toString(),
                    value: action.lhs.Value.value.toString()
                }
                return {
                    lhs: JSON.stringify(lhs, null, 2),
                    rhs: "" // Show empty because deleted, no next value
                }
            }
            // Edited entry
            case "E": {
                const lhs = {
                    name: action.lhs.Name.value.toString(),
                    value: action.lhs.Value.value.toString()
                }
                const rhs = {
                    name: action.rhs.Name.value.toString(),
                    value: action.rhs.Value.value.toString()
                }
                return {
                    lhs: JSON.stringify(lhs, null, 2),
                    rhs: JSON.stringify(rhs, null, 2)
                }
            }
            // New entry
            case "N": {
                const rhs = {
                    name: action.rhs.Name.value.toString(),
                    value: action.rhs.Value.value.toString()
                }
                return {
                    lhs: "", // Show empty because new entry, no previous value
                    rhs: JSON.stringify(rhs, null, 2)
                }
            }
            default:
                return null;
        }
    }

    private isDiffEditObject = (kind: DiffEditObject): kind is DiffEdit<DiffValue, DiffValue> => (kind as DiffEdit<DiffValue, DiffValue>).rhs.value !== undefined;

    private getParameterDiff = (diffObj: DiffArray<DiffArrayNameValue, DiffArrayNameValue> | DiffEditObject) => {
        const paramName = diffObj.path?.slice(1)[0];
        if (this.isDiffArray(diffObj)) {
            const value = this.getDiffArrayValue(diffObj.item);
            return (
                <DiffMonacoEditor
                    original={value?.lhs ?? paramName}
                    modified={value?.rhs ?? paramName}
                    mode={ParameterEditorMode.csharp}
                    options={{ readOnly: true, contextmenu: false, enableSplitViewResizing: false }}
                />
            )
        } else {
            const isSingleValueObject = this.isDiffEditObject(diffObj);
            const original = isSingleValueObject ? diffObj.lhs?.value : diffObj.lhs?.toString();
            const modified = isSingleValueObject ? diffObj.rhs?.value : diffObj.rhs?.toString();
            return (
                <DiffMonacoEditor
                    original={original ?? paramName}
                    modified={modified ?? paramName}
                    mode={ParameterEditorMode.csharp}
                    options={{ readOnly: true, contextmenu: false, enableSplitViewResizing: false }}
                />
            )
        }

    }

    public render() {
        if (!this.state.selectedElementId) {
            return null;
        }

        return (
            <CustomModal
                label={"overlay-" + this.state.selectedElementId}
                onClose={this.closeModal}
                modalTitle={<h2>{FRENDS.Localizations.Properties}</h2>}
                shouldCloseOnOverlayClick={true}
                show={true}
                key={this.state.selectedElementId}
                size="large"
            >
                <S.FormGroup>
                    <S.SimpleTable>
                        <thead>
                            <tr>
                                <th>
                                    <S.FlexContainer justifySpaceBetween={true}>
                                        <span>{`v.${this.props.processLeft?.majorVersion}.${this.props.processLeft?.minorVersion}.${this.props.processLeft?.version}`}</span>
                                        <span>{`v.${this.props.processRight?.majorVersion}.${this.props.processRight?.minorVersion}.${this.props.processRight?.version}`}</span>
                                    </S.FlexContainer>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.props.parameterDiff && this.props.parameterDiff[this.state.selectedElementId]
                                .map((diffObj, index) => (
                                    <tr key={this.state.selectedElementId && this.state.selectedElementId + index}>
                                        <td>
                                            <h4>{diffObj.path?.slice(1).join(".")}</h4>
                                            {this.getParameterDiff(diffObj)}
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </S.SimpleTable>
                </S.FormGroup>
                <S.ButtonGroup>
                    <S.Button onClick={this.closeModal}>Close</S.Button>
                </S.ButtonGroup>
            </CustomModal>
        );
    }

    public openModal = (elementId: string | null) => {
        if (this.props.parameterDiff && this.props.parameterDiff[elementId as string]) {
            this.setState({
                selectedElementId: elementId
            });
        }
    }

    private closeModal = () => {
        this.setState({
            selectedElementId: null
        });
    }
}

interface DiffValue {
    value: string;
    mode: string;
}

interface DiffArrayNameValue {
    Name: DiffValue;
    Value: DiffValue;
}

type DiffEditObject = DiffEdit<unknown | DiffValue, unknown | DiffValue>;

export interface DiffModalProps {
    processLeft: IProcessData | null;
    processRight: IProcessData | null;
    parameterDiff: { [key: string]: Array<DiffEditObject> | Array<DiffArray<DiffArrayNameValue, DiffArrayNameValue>> } | null;
}

export interface DiffModalState {
    selectedElementId: string | null;
}
